import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Button, Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import { getRecords, uploadExelFile } from "../../../services/api.service";

const breadcrumbItems = [];

const OrdertLogList = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    getData();
  }, [searchKeyword, page, pageSize, selectedFile]);

  const getData = async () => {
    const projectId = JSON.parse(localStorage?.getItem("projectID"));
    try {
      const payload = {
        modelName: "orders",
        whereCondition: {
          project_id: projectId,
        },
        relations: [
          {
            module: "projects",
            moduleAs: "project",
          },
          {
            module: "company_masters",
            moduleAs: "company",
          },
          {
            module: "order_documents",
            moduleAs: "order_documents",
          },
        ],
        search: {
          searchKeyword: searchKeyword,
          field_name: [
            "project.project_code",
            "order_no",
            "company.company_name",
            "project.project_name",
          ],
        },
        pagination: {
          page: page + 1,
          pageSize: pageSize,
        },
      };

      const responseData = await getRecords(payload, "/listOrder");

      // Assign an `index` property starting from 1
      responseData.data.forEach((element, index) => {
        element.index = index + 1;
      });
      setTotalCount(responseData.count);

      const sortedData = responseData.data.sort((a, b) => b.index - a.index);
      setData(sortedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickAdd = () => {
    navigate("/order-create");
  };

  const handleEditClick = (id) => {
    navigate(`/order-create/${id}`);
  };

  const handleViewClick = (id) => {
    navigate(`/order-log-view/${id}`);
  };

  const getSearchInputData = (inputValue) => {
    setSearchKeyword(inputValue);
  };

  const nextToPages = () => {
    setPage(page + 1);
  };

  const previousToPages = () => {
    setPage(page - 1);
  };

  const handlePageSizeChange = (data) => {
    setPageSize(data);
    const newTotalPages = Math.ceil(totalCount / data);
    if (page >= newTotalPages) {
      setPage(0);
    }
  };

  const lastToPages = () => {
    setPage(Math.ceil(totalCount / pageSize) - 1);
  };

  const previousFirstToPages = () => {
    setPage(0);
  };
  const columns = useMemo(
    () => [
      {
        Header: "SR.NO",
        accessor: "index",
        accessor: (row) => page * pageSize + row.index,
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "50px" }}>{value}</div>,
      },

      {
        Header: "PROJECT",
        accessor: (row) =>
          row?.project && row?.project?.project_name
            ? row?.project?.project_name
            : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "PROJECT CODE",
        accessor: (row) =>
          row?.project && row?.project?.project_code
            ? row?.project?.project_code
            : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "SO No",
        accessor: (row) =>
          row?.order_no && row?.order_no ? row?.order_no : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ORDER REFERENCE NO",
        accessor: (row) => (row && row?.order_ref_no ? row?.order_ref_no : "-"),
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "CUSTOMER",
        accessor: (row) =>
          row?.company && row?.company ? row?.company?.company_name : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ORDER DATE",
        accessor: (row) =>
          row?.order_date ? moment(row?.order_date).format("DD-MM-YYYY") : "-",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "100px" }}>{value}</div>,
      },
      {
        Header: "ORDER STATUS",
        accessor: (row) => (
          <span className="badge bg-success-subtle text-success text-uppercase">
            {row?.status?.split("_").join(" ")}
          </span>
        ),
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "STATUS",
        accessor: "is_active",
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => (
          <div style={{ width: "100px" }}>
            {value === "Active" || value === false || value === 0 ? (
              <span className="badge bg-danger-subtle text-danger text-uppercase">
                In-active
              </span>
            ) : (
              <span className="badge bg-success-subtle text-success text-uppercase">
                Active
              </span>
            )}
          </div>
        ),
      },

      {
        Header: "ACTION",
        accessor: "actions",
        disableFilters: true,
        filterable: false,
        Cell: ({ row }) => (
          <div
            style={{
              width: "150px",
              display: "flex",
            }}
          >
            <div
              onClick={() => {
                handleViewClick(row.original.id);
              }}
            >
              <Link className="me-3 text-primary">
                <i className="mdi mdi-eye font-size-18"></i>
              </Link>
            </div>
          </div>
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumb title="Order List" breadcrumbItems={breadcrumbItems} />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                isPagination={false}
                // isGlobalFilter={false}
                iscustomPageSize={false}
                isBordered={false}
                customPageSize={10}
                isLoading={isLoading}
                onSearchInputData={getSearchInputData}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChange}
                nextToPage={nextToPages}
                lastToPage={lastToPages}
                previousToPage={previousToPages}
                previousFirstToPage={previousFirstToPages}
                totalCount={totalCount}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OrdertLogList;
